import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email.trim(), password);
      toast.success("Signed in");
      navigate(user.role === "admin" ? "/" : (user.athlete_id ? `/athlete/${user.athlete_id}` : "/"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background grain">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#070707] border-r border-border overflow-hidden">
        <Logo size={44} />
        <div className="relative z-10">
          <p className="overline mb-4">NIL client portal · back office</p>
          <h1 className="font-display-tight text-6xl xl:text-7xl text-white leading-[0.85]">
            Track the<br />growth.<br />
            <span className="text-[#E1FF00]">Own the</span><br />narrative.
          </h1>
          <p className="text-muted-foreground mt-6 max-w-md leading-relaxed">
            The private portal for Merch Run's football and basketball clients. Verified
            profiles, real week-over-week momentum, and brand-ready media kits.
          </p>
        </div>
        <div className="text-xs text-muted-foreground/60">© {new Date().getFullYear()} The Merch Dash</div>
        <div
          className="pointer-events-none absolute -right-32 -bottom-32 h-96 w-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(225,255,0,0.08), transparent 70%)" }}
        />
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={40} /></div>
          <p className="overline mb-2">Sign in</p>
          <h2 className="font-display text-3xl text-white mb-8">Welcome back</h2>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
                className="bg-secondary border-border h-11"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-white transition-colors" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="bg-secondary border-border h-11"
              />
            </div>

            {error && (
              <div className="text-sm text-[#FF6666] bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground/70 mt-8 leading-relaxed">
            Access is invitation-only. New team members and athletes receive a secure link
            from a Merch Run admin. No public sign-up.
          </p>
        </div>
      </div>
    </div>
  );
}
